/**
 * Dialog Premium para conexão de WhatsApp via QR Code ou Pairing Code
 * 
 * Utiliza arquitetura multi-provider via useProviderUtils.
 * Design premium com branding WhatsApp, animações suaves e UX humanizada.
 * 
 * Implementa recomendações Z-API/WAHA:
 * - Refresh automático a cada 15 segundos (dentro do limite 10-20s)
 * - Máximo 3 tentativas automáticas antes de pausar
 * - Botão manual para reiniciar ciclo de tentativas
 * - Pairing Code como alternativa ao QR Code (WAHA)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { 
  Loader2, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Smartphone, 
  QrCode, 
  Hash,
  Shield,
  Copy,
  Check,
  Menu,
  Link2,
  MessageCircle,
  Power
} from 'lucide-react';
import { toast } from 'sonner';
import { useProviderUtils } from '@/hooks/useProviderUtils';
import { cn } from '@/lib/utils';

interface QRCodeConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxId: string;
  inboxName: string;
  // Número do WhatsApp configurado na inbox (será pré-preenchido na aba Código)
  inboxIdentifier?: string;
  onConnected: () => void;
  // Slug do provider para detectar suporte a pairing code
  providerSlug?: string;
  // Callback para ativar inbox quando está inativa (retorna Promise para aguardar ativação)
  onActivateInbox?: () => Promise<void>;
}

// Estados possíveis da conexão
type ConnectionState = 
  | 'loading' 
  | 'qrcode' 
  | 'scanning' 
  | 'connected' 
  | 'error' 
  | 'paused'
  | 'pairing_input'    // Aguardando usuário inserir telefone
  | 'pairing_loading'  // Solicitando código ao servidor
  | 'pairing_code'     // Exibindo código para usuário digitar no WhatsApp
  | 'inactive_blocked'; // Inbox desativada - requer ativação antes de conectar

// Método de autenticação
type AuthMethod = 'qrcode' | 'pairing';

// Constantes de configuração (recomendações Z-API)
const QR_REFRESH_INTERVAL = 15000;  // 15 segundos (entre 10-20s recomendado)
const STATUS_POLL_INTERVAL = 3000;   // 3 segundos para verificar conexão
const MAX_AUTO_ATTEMPTS = 3;         // Máximo de tentativas automáticas
const PAIRING_CODE_EXPIRATION = 60;  // 60 segundos para expiração do pairing code

// Providers que suportam pairing code
const PAIRING_CODE_PROVIDERS = ['waha'];

// Cor oficial do WhatsApp
const WHATSAPP_COLOR = '#25D366';

export function QRCodeConnectionDialog({
  open,
  onOpenChange,
  inboxId,
  inboxName,
  inboxIdentifier,
  onConnected,
  providerSlug,
  onActivateInbox,
}: QRCodeConnectionDialogProps) {
  // Hook multi-provider para operações de conexão
  const { getQRCode, getConnectionStatus, requestPairingCode } = useProviderUtils();

  // Estados do componente - QR Code
  const [state, setState] = useState<ConnectionState>('loading');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [countdown, setCountdown] = useState(QR_REFRESH_INTERVAL / 1000);

  // Estados do componente - Pairing Code
  const [authMethod, setAuthMethod] = useState<AuthMethod>('qrcode');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pairingCountdown, setPairingCountdown] = useState(PAIRING_CODE_EXPIRATION);
  
  // Estado para loading da ativação de inbox
  const [isActivating, setIsActivating] = useState(false);

  // Refs para controle de intervals
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pairingCountdownRef = useRef<NodeJS.Timeout | null>(null);

  // Verifica se o provider suporta pairing code
  const supportsPairingCode = providerSlug && PAIRING_CODE_PROVIDERS.includes(providerSlug);

  /**
   * Limpa todos os intervals e timeouts ativos
   */
  const clearAllTimers = useCallback(() => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (pairingCountdownRef.current) {
      clearInterval(pairingCountdownRef.current);
      pairingCountdownRef.current = null;
    }
  }, []);

  /**
   * Busca QR Code via provider-proxy (multi-provider)
   */
  const fetchQRCode = useCallback(async (isManualRetry = false) => {
    // Se for retry manual, reseta contador de tentativas
    if (isManualRetry) {
      setAttempts(0);
    }

    setState('loading');
    setError(null);
    clearAllTimers();

    try {
      console.log('[QRCodeDialog] Buscando QR Code para inbox:', inboxId);
      const result = await getQRCode(inboxId);

      // NOVO: Detectar inbox inativa (statusProtected)
      // Exibe tela intuitiva ao invés de erro técnico
      if (result.statusProtected) {
        console.log('[QRCodeDialog] Inbox inativa detectada via statusProtected');
        setState('inactive_blocked');
        setError('Inbox desativada');
        return;
      }

      // Se já está conectado, finaliza
      if (result.connected) {
        setState('connected');
        toast.success('WhatsApp conectado com sucesso!');
        onConnected();
        return;
      }

      // Se obteve QR Code com sucesso
      if (result.qrcode) {
        setQrCode(result.qrcode);
        setState('qrcode');
        setAttempts(prev => prev + 1);
        setCountdown(QR_REFRESH_INTERVAL / 1000);
        return;
      }

      // Se está no estado de scanning (usuário escaneou, aguardando confirmação)
      if (result.status === 'scanning') {
        console.log('[QRCodeDialog] Usuário escaneou QR, aguardando confirmação no celular');
        setState('scanning');
        return;
      }

      // Se houve erro
      throw new Error(result.error || 'QR Code não disponível. Verifique as credenciais.');

    } catch (err) {
      console.error('[QRCodeDialog] Erro ao buscar QR Code:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setState('error');
    }
  }, [inboxId, getQRCode, onConnected, clearAllTimers]);

  /**
   * Inicia countdown de expiração do pairing code
   */
  const startPairingCountdown = useCallback(() => {
    // Limpa timer anterior se existir
    if (pairingCountdownRef.current) {
      clearInterval(pairingCountdownRef.current);
    }

    setPairingCountdown(PAIRING_CODE_EXPIRATION);
    pairingCountdownRef.current = setInterval(() => {
      setPairingCountdown(prev => {
        if (prev <= 1) {
          // Código expirou - volta para input
          if (pairingCountdownRef.current) {
            clearInterval(pairingCountdownRef.current);
            pairingCountdownRef.current = null;
          }
          setPairingCode(null);
          setState('pairing_input');
          toast.info('Código expirou. Gere um novo.');
          return PAIRING_CODE_EXPIRATION;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /**
   * Solicita código de pareamento (alternativa ao QR Code)
   */
  const handleRequestPairingCode = useCallback(async () => {
    if (!phoneNumber.trim()) {
      toast.error('Digite o número de telefone');
      return;
    }

    setState('pairing_loading');
    setError(null);
    setPairingCode(null);

    try {
      console.log('[QRCodeDialog] Solicitando pairing code para inbox:', inboxId);
      const result = await requestPairingCode(inboxId, phoneNumber);

      if (!result.success) {
        throw new Error(result.error || 'Erro ao solicitar código de pareamento');
      }

      if (result.code) {
        setPairingCode(result.code);
        setState('pairing_code');
        toast.success('Código gerado! Digite-o no seu WhatsApp');
        // Inicia countdown de expiração
        startPairingCountdown();
      } else {
        throw new Error('Código não retornado pelo servidor');
      }
    } catch (err) {
      console.error('[QRCodeDialog] Erro ao solicitar pairing code:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setState('error');
    }
  }, [inboxId, phoneNumber, requestPairingCode, startPairingCountdown]);

  /**
   * Verifica status da conexão via provider-proxy (multi-provider)
   */
  const checkStatus = useCallback(async (): Promise<boolean> => {
    try {
      const result = await getConnectionStatus(inboxId);
      console.log('[QRCodeDialog] Status recebido:', result);

      if (result.status === 'active') {
        setState('connected');
        clearAllTimers();
        toast.success('WhatsApp conectado com sucesso!');
        onConnected();
        return true;
      }

      return false;
    } catch (err) {
      console.error('[QRCodeDialog] Erro ao verificar status:', err);
      return false;
    }
  }, [inboxId, getConnectionStatus, onConnected, clearAllTimers]);

  /**
   * Inicia ciclo de refresh automático do QR Code
   * GUARD: Não cria novos timers se já existem ativos
   */
  const startRefreshCycle = useCallback(() => {
    // Guard: evita múltiplos ciclos simultâneos
    if (refreshTimeoutRef.current || countdownIntervalRef.current) {
      console.log('[QRCodeDialog] Ciclo de refresh já ativo, ignorando');
      return;
    }
    
    if (attempts >= MAX_AUTO_ATTEMPTS) {
      console.log('[QRCodeDialog] Máximo de tentativas atingido, pausando');
      setState('paused');
      clearAllTimers();
      return;
    }

    console.log(`[QRCodeDialog] Iniciando ciclo de refresh (tentativa ${attempts}/${MAX_AUTO_ATTEMPTS})`);
    
    refreshTimeoutRef.current = setTimeout(() => {
      if (state === 'qrcode') {
        console.log(`[QRCodeDialog] Refresh automático`);
        fetchQRCode(false);
      }
    }, QR_REFRESH_INTERVAL);

    setCountdown(QR_REFRESH_INTERVAL / 1000);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

  }, [attempts, state, fetchQRCode, clearAllTimers]);

  /**
   * Inicia polling de status da conexão
   * GUARD: Não cria novo interval se já existe um ativo
   */
  const startStatusPolling = useCallback(() => {
    // Guard: evita múltiplos intervals simultâneos
    if (statusIntervalRef.current) {
      console.log('[QRCodeDialog] Polling de status já ativo, ignorando');
      return;
    }
    
    console.log('[QRCodeDialog] Iniciando polling de status');
    statusIntervalRef.current = setInterval(async () => {
      const connected = await checkStatus();
      if (connected) {
        clearAllTimers();
      }
    }, STATUS_POLL_INTERVAL);
  }, [checkStatus, clearAllTimers]);

  /**
   * Alterna para método QR Code
   */
  const switchToQRCode = useCallback(() => {
    setAuthMethod('qrcode');
    setPairingCode(null);
    setPhoneNumber('');
    setError(null);
    fetchQRCode(true);
  }, [fetchQRCode]);

  /**
   * Alterna para método Pairing Code
   */
  const switchToPairingCode = useCallback(() => {
    setAuthMethod('pairing');
    setQrCode(null);
    setError(null);
    clearAllTimers();
    setState('pairing_input');
  }, [clearAllTimers]);

  /**
   * Copia código para clipboard
   */
  const handleCopyCode = useCallback(async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      toast.success('Código copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Falha ao copiar');
    }
  }, [pairingCode]);

  // Efeito: Busca QR Code quando dialog abre e pré-preenche telefone da inbox
  // NOTA: Separado do efeito de ciclos para evitar loops de dependências
  useEffect(() => {
    if (open) {
      console.log('[QRCodeDialog] Diálogo aberto, inicializando...');
      setAuthMethod('qrcode');
      // Pré-preenche telefone com identifier da inbox (configurado nas settings)
      if (inboxIdentifier) {
        setPhoneNumber(inboxIdentifier);
      }
      fetchQRCode(true);
    } else {
      console.log('[QRCodeDialog] Diálogo fechado, limpando estado...');
      clearAllTimers();
      setQrCode(null);
      setPairingCode(null);
      setPhoneNumber('');
      setState('loading');
      setError(null);
      setAttempts(0);
      setCountdown(QR_REFRESH_INTERVAL / 1000);
      setPairingCountdown(PAIRING_CODE_EXPIRATION);
      setCopied(false);
    }
    // NOTA: Não incluímos fetchQRCode nas deps para evitar loop
    // fetchQRCode é chamado apenas quando open muda para true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inboxIdentifier]);

  // Efeito: Gerencia ciclo de refresh e polling baseado no estado
  // NOTA: Guards nas funções evitam recriação de timers desnecessária
  useEffect(() => {
    const statusPollingStates: ConnectionState[] = ['qrcode', 'scanning', 'pairing_code'];
    
    // Só processa se aberto e em estado válido
    if (!open || !statusPollingStates.includes(state)) {
      return;
    }
    
    console.log(`[QRCodeDialog] Estado mudou para: ${state}, iniciando ciclos...`);
    
    // Inicia polling de status (guard interno evita duplicação)
    startStatusPolling();
    
    // Inicia ciclo de refresh apenas para QR Code
    if (state === 'qrcode') {
      startRefreshCycle();
    }

    // Cleanup: limpa apenas ao sair do estado ou fechar
    return () => {
      console.log('[QRCodeDialog] Cleanup do efeito de ciclos');
      // Não limpa timers aqui - deixa o cleanup do efeito de open fazer isso
      // Isso evita o loop: cleanup -> recria -> cleanup -> recria
    };
    // NOTA: Funções têm guards internos, não causam reexecução infinita
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, open]);

  // Calcula progresso do countdown
  const countdownProgress = (countdown / (QR_REFRESH_INTERVAL / 1000)) * 100;

  /**
   * Retorna número de telefone limpo (apenas dígitos)
   * O PhoneInput já retorna o valor completo com código do país
   */
  const getCleanPhoneNumber = (): string => {
    return phoneNumber.replace(/\D/g, '');
  };

  // Calcula cor do timer baseado no tempo restante
  const getTimerColor = () => {
    if (countdown <= 3) return 'text-red-500';
    if (countdown <= 7) return 'text-amber-500';
    return 'text-emerald-500';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden bg-card border-border">
        {/* Header Premium com Branding WhatsApp */}
        <div className="relative px-6 pt-6 pb-4">
          {/* Background gradiente sutil */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{ 
              background: `linear-gradient(135deg, ${WHATSAPP_COLOR} 0%, transparent 60%)`
            }}
          />
          
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* Ícone WhatsApp estilizado */}
              <div 
                className="h-12 w-12 rounded-xl flex items-center justify-center shadow-lg"
                style={{ backgroundColor: WHATSAPP_COLOR }}
              >
                <MessageCircle className="h-6 w-6 text-white" fill="white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Conectar WhatsApp
                </h2>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {inboxName}
                </p>
              </div>
            </div>
            
            {/* Badge de segurança */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Criptografado
              </span>
            </div>
          </div>
        </div>

        {/* Tabs Premium (se suporta pairing code) */}
        {supportsPairingCode && (
          <div className="px-6">
            <div className="flex gap-2 p-1 rounded-xl bg-muted/50">
              <button
                onClick={switchToQRCode}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200",
                  authMethod === 'qrcode'
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <QrCode className="h-4 w-4" />
                QR Code
              </button>
              <button
                onClick={switchToPairingCode}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200",
                  authMethod === 'pairing'
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Hash className="h-4 w-4" />
                Código
              </button>
            </div>
          </div>
        )}

        {/* Conteúdo Principal */}
        <div className="px-6 pb-6 pt-4">
          {authMethod === 'qrcode' ? renderQRCodeContent() : renderPairingCodeContent()}
        </div>
      </DialogContent>
    </Dialog>
  );

  /**
   * Renderiza conteúdo do QR Code
   */
  function renderQRCodeContent() {
    return (
      <div className="flex flex-col items-center">
        {/* Estado: Carregando */}
        {state === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="relative">
              <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">Gerando QR Code...</p>
          </div>
        )}

        {/* Estado: QR Code disponível */}
        {state === 'qrcode' && qrCode && (
          <div className="flex flex-col items-center gap-5 w-full">
            {/* QR Code com moldura premium estilo scanner */}
            <div className="relative">
              {/* Cantos decorativos do scanner */}
              <div className="absolute -inset-3 pointer-events-none">
                {/* Canto superior esquerdo */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 rounded-tl-lg" style={{ borderColor: WHATSAPP_COLOR }} />
                {/* Canto superior direito */}
                <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 rounded-tr-lg" style={{ borderColor: WHATSAPP_COLOR }} />
                {/* Canto inferior esquerdo */}
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 rounded-bl-lg" style={{ borderColor: WHATSAPP_COLOR }} />
                {/* Canto inferior direito */}
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 rounded-br-lg" style={{ borderColor: WHATSAPP_COLOR }} />
              </div>
              
              {/* Container do QR Code com animação de pulse suave */}
              <div className="relative p-3 rounded-xl bg-white shadow-lg animate-[pulse_3s_ease-in-out_infinite]">
                <img
                  src={qrCode}
                  alt="QR Code para conexão WhatsApp"
                  className="w-56 h-56 rounded-lg"
                />
              </div>
              
              {/* Indicador de ativo */}
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-4 w-4">
                  <span 
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                  <span 
                    className="relative inline-flex rounded-full h-4 w-4"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                </span>
              </div>
            </div>

            {/* Instruções em Steps Visuais */}
            <div className="w-full space-y-2">
              <InstructionStep number={1} icon={Smartphone} text="Abra o WhatsApp no celular" />
              <InstructionStep number={2} icon={Menu} text="Toque em Menu (⋮) → Aparelhos conectados" />
              <InstructionStep number={3} icon={Link2} text="Conectar aparelho → Escanear QR Code" />
            </div>

            {/* Timer Circular Premium */}
            <div className="flex items-center justify-between w-full px-2">
              <div className="flex items-center gap-2">
                <CircularTimer progress={countdownProgress} size={32} />
                <span className={cn("text-sm font-medium transition-colors", getTimerColor())}>
                  {countdown}s
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>Tentativa {attempts}/{MAX_AUTO_ATTEMPTS}</span>
              </div>
            </div>

            {/* Botão de refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchQRCode(false)}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar QR Code
            </Button>
          </div>
        )}

        {/* Estado: Scanning */}
        {state === 'scanning' && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="relative">
              <div 
                className="h-20 w-20 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${WHATSAPP_COLOR}15` }}
              >
                <Smartphone className="h-10 w-10" style={{ color: WHATSAPP_COLOR }} />
              </div>
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-4 w-4">
                  <span 
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                  <span 
                    className="relative inline-flex rounded-full h-4 w-4"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                </span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">Confirmando conexão...</p>
              <p className="text-sm text-muted-foreground mt-1">
                Verifique seu celular para confirmar
              </p>
            </div>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Estado: Pausado */}
        {state === 'paused' && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="h-20 w-20 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <RefreshCw className="h-10 w-10 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">QR Code expirou</p>
              <p className="text-sm text-muted-foreground mt-1">
                O tempo limite foi atingido após {MAX_AUTO_ATTEMPTS} tentativas
              </p>
            </div>
            <Button onClick={() => fetchQRCode(true)} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Gerar novo QR Code
            </Button>
          </div>
        )}

        {/* Estado: Conectado */}
        {state === 'connected' && renderConnectedState()}

        {/* Estado: Erro */}
        {state === 'error' && renderErrorState()}
        
        {/* Estado: Inbox Inativa (bloqueio de proteção) */}
        {state === 'inactive_blocked' && renderInactiveState()}
      </div>
    );
  }

  /**
   * Renderiza conteúdo do Pairing Code
   */
  function renderPairingCodeContent() {
    return (
      <div className="flex flex-col items-center">
        {/* Estado: Input de telefone */}
        {state === 'pairing_input' && (
          <div className="flex flex-col gap-5 w-full">
            <div className="text-center">
              <p className="text-base font-medium text-foreground">Conectar por código</p>
              <p className="text-sm text-muted-foreground mt-1">
                Digite o número do WhatsApp que deseja conectar
              </p>
            </div>

            {/* Input com seletor de país */}
            <div className="space-y-3">
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                placeholder="(11) 99999-9999"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground text-center">
                Selecione o país e digite o número
              </p>
            </div>

            <Button 
              onClick={handleRequestPairingCode}
              disabled={getCleanPhoneNumber().length < 12}
              className="w-full h-11 gap-2"
              style={{ backgroundColor: WHATSAPP_COLOR }}
            >
              <Hash className="h-4 w-4" />
              Gerar código de pareamento
            </Button>
          </div>
        )}

        {/* Estado: Carregando código */}
        {state === 'pairing_loading' && (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Gerando código de pareamento...</p>
          </div>
        )}

        {/* Estado: Exibindo código */}
        {state === 'pairing_code' && pairingCode && (
          <div className="flex flex-col items-center gap-5 w-full">
            {/* Código em cards separados */}
            <div className="relative">
              <div className="flex gap-2">
                {pairingCode.split('-').map((group, idx) => (
                  <div
                    key={idx}
                    className="bg-muted/50 border border-border rounded-xl px-4 py-3 animate-scale-in"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <span className="text-2xl font-mono font-bold tracking-wider text-foreground">
                      {group}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Indicador de ativo */}
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-4 w-4">
                  <span 
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                  <span 
                    className="relative inline-flex rounded-full h-4 w-4"
                    style={{ backgroundColor: WHATSAPP_COLOR }}
                  />
                </span>
              </div>
            </div>

            {/* Timer de expiração */}
            <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-muted/30 border border-border/50">
              <CircularTimer progress={(pairingCountdown / PAIRING_CODE_EXPIRATION) * 100} />
              <span className="text-sm text-muted-foreground">
                Expira em <span className="font-mono font-medium text-foreground">{pairingCountdown}s</span>
              </span>
            </div>

            {/* Botão Copiar */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyCode}
              className="gap-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-emerald-500" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar código
                </>
              )}
            </Button>

            {/* Instruções em Steps Visuais */}
            <div className="w-full space-y-2">
              <InstructionStep number={1} icon={Smartphone} text="Abra o WhatsApp no celular" />
              <InstructionStep number={2} icon={Menu} text="Menu (⋮) → Aparelhos conectados" />
              <InstructionStep number={3} icon={Link2} text="Conectar com número de telefone" />
              <InstructionStep number={4} icon={Hash} text="Digite o código acima" />
            </div>

            {/* Ações */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Limpa timer de expiração do código anterior
                  if (pairingCountdownRef.current) {
                    clearInterval(pairingCountdownRef.current);
                    pairingCountdownRef.current = null;
                  }
                  setPairingCode(null);
                  setPairingCountdown(PAIRING_CODE_EXPIRATION);
                  setState('pairing_input');
                }}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Novo código
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              A conexão será detectada automaticamente
            </p>
          </div>
        )}

        {/* Estado: Conectado */}
        {state === 'connected' && renderConnectedState()}

        {/* Estado: Erro */}
        {state === 'error' && renderErrorState()}
      </div>
    );
  }

  /**
   * Renderiza estado de conectado (compartilhado)
   */
  function renderConnectedState() {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        {/* Ícone com animação de sucesso */}
        <div className="relative">
          <div 
            className="h-20 w-20 rounded-2xl flex items-center justify-center animate-scale-in"
            style={{ backgroundColor: `${WHATSAPP_COLOR}15` }}
          >
            <CheckCircle2 
              className="h-10 w-10 animate-[bounce_0.5s_ease-out]" 
              style={{ color: WHATSAPP_COLOR }} 
            />
          </div>
          {/* Círculos decorativos de celebração */}
          <div 
            className="absolute -inset-4 rounded-full opacity-20 animate-ping"
            style={{ backgroundColor: WHATSAPP_COLOR }}
          />
        </div>
        <div className="text-center">
          <p 
            className="text-xl font-semibold"
            style={{ color: WHATSAPP_COLOR }}
          >
            Conectado com sucesso! 🎉
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Seu WhatsApp está pronto para receber mensagens
          </p>
        </div>
        <Button 
          onClick={() => onOpenChange(false)} 
          className="gap-2"
          style={{ backgroundColor: WHATSAPP_COLOR }}
        >
          Fechar
        </Button>
      </div>
    );
  }

  /**
   * Renderiza estado de erro (compartilhado)
   */
  function renderErrorState() {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="h-20 w-20 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <XCircle className="h-10 w-10 text-destructive" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-destructive">Erro na conexão</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">{error}</p>
        </div>
        <Button 
          onClick={() => authMethod === 'qrcode' ? fetchQRCode(true) : setState('pairing_input')} 
          variant="outline" 
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  /**
   * Renderiza estado de inbox inativa (bloqueio de proteção)
   * Exibe mensagem clara ao usuário e permite ativar a inbox diretamente
   */
  function renderInactiveState() {
    // Handler para ativar inbox e tentar reconectar
    const handleActivateAndConnect = async () => {
      if (!onActivateInbox) {
        // Se não tem callback, fecha o dialog
        onOpenChange(false);
        return;
      }

      setIsActivating(true);
      try {
        await onActivateInbox();
        // Após ativar, tenta buscar QR Code novamente
        toast.success('Inbox ativada! Buscando QR Code...');
        fetchQRCode(true);
      } catch (err) {
        console.error('[QRCodeDialog] Erro ao ativar inbox:', err);
        toast.error('Erro ao ativar inbox. Tente novamente.');
      } finally {
        setIsActivating(false);
      }
    };

    return (
      <div className="flex flex-col items-center gap-5 py-6">
        {/* Ícone de Power/Desligado - tom âmbar para indicar ação necessária */}
        <div className="h-20 w-20 rounded-2xl bg-amber-500/10 flex items-center justify-center">
          <Power className="h-10 w-10 text-amber-600 dark:text-amber-400" />
        </div>
        
        {/* Mensagem clara para o usuário */}
        <div className="text-center">
          <p className="text-lg font-semibold text-foreground">
            Inbox Desativada
          </p>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs">
            Para conectar ao WhatsApp, é necessário ativar a inbox primeiro.
          </p>
        </div>

        {/* Botões de ação */}
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {/* Botão primário - Ativar inbox */}
          <Button 
            onClick={handleActivateAndConnect}
            disabled={isActivating}
            className="gap-2 w-full"
            style={{ backgroundColor: WHATSAPP_COLOR }}
          >
            {isActivating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Ativando...
              </>
            ) : (
              <>
                <Power className="h-4 w-4" />
                Ativar e Conectar
              </>
            )}
          </Button>
          
          {/* Botão secundário - Fechar */}
          <Button 
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="w-full"
            disabled={isActivating}
          >
            Fechar
          </Button>
        </div>
      </div>
    );
  }
}

/**
 * Componente de Step de Instrução
 */
interface InstructionStepProps {
  number: number;
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}

function InstructionStep({ number, icon: Icon, text }: InstructionStepProps) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
      <div 
        className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
        style={{ backgroundColor: WHATSAPP_COLOR }}
      >
        {number}
      </div>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-sm text-foreground">{text}</span>
    </div>
  );
}

/**
 * Componente de Timer Circular
 */
interface CircularTimerProps {
  progress: number;
  size?: number;
}

function CircularTimer({ progress, size = 32 }: CircularTimerProps) {
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  // Cor baseada no progresso
  const getColor = () => {
    if (progress <= 20) return '#ef4444'; // vermelho
    if (progress <= 50) return '#f59e0b'; // amarelo
    return WHATSAPP_COLOR; // verde WhatsApp
  };

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Círculo de fundo */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/30"
      />
      {/* Círculo de progresso */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={getColor()}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-1000 ease-linear"
      />
    </svg>
  );
}
